import { FC, useState } from "react";
import {
    Box,
    Icon,
    Picture,
    PictureProps,
    Text,
    useBreakpointValue,
} from "@smile2impress/components/relaunch";
import { CornerIcon, Flex } from "@smile2impress/components";
import { DirectionType } from "templates/relaunch/MultiColumnSlider/MultiColumnSlider.types";

import { IMAGE_SIZE } from "./constants";
import { UniversalImageElementProps } from "./UniversalImageElement.types";

export const UniversalImageElement: FC<UniversalImageElementProps> = ({
    image,
    withFrame = false,
    withFrameBorders = false,
    withExpansion = false,
    captions = [],
    direction = DirectionType.Vertical,
}) => {
    const displayFrame = withFrame && withFrameBorders && !Array.isArray(image);
    const splittedImages = Array.isArray(image) && image.length === 2;
    const hasHorizontalDirection = direction === DirectionType.Horizontal;
    const [splittedSizes, setSizes] = useState<number[]>([1 / 2, 1 / 2]);

    const isMobile = useBreakpointValue({
        base: true,
        tablet: true,
        desktop: false,
    });

    const [captionLeft, captionRight] = captions;

    const onImageSelected = (left: boolean) => {
        if (left) setSizes([2 / 3, 1 / 3]);
        else {
            setSizes([1 / 3, 2 / 3]);
        }
    };

    const onElementLeave = () => {
        setSizes([1 / 2, 1 / 2]);
    };

    const Caption = ({ text }: { text: string }) => (
        <Box
            position="absolute"
            left="0"
            right="0"
            bottom={1}
            textAlign="center"
        >
            <Text
                bg="rgba(0, 0, 0, 0.15)"
                backdropFilter="blur(44px)"
                px={1}
                py="8px"
                borderRadius="md"
                display="inline-block"
                color="text.baseInverse"
                textStyle={["body", "body", "bodySmall"]}
            >
                {text}
            </Text>
        </Box>
    );

    return (
        <Flex
            aspectRatio={[
                "auto",
                "auto",
                splittedImages ? "auto" : "1/1",
                splittedImages ? "auto" : "1/1",
            ]}
            alignContent="center"
            position="relative"
        >
            {displayFrame && (
                <>
                    <Icon
                        as={CornerIcon}
                        position="absolute"
                        w={3}
                        h={3}
                        top={0}
                        left={0}
                    />
                    <Icon
                        as={CornerIcon}
                        position="absolute"
                        w={3}
                        h={3}
                        top={0}
                        right={0}
                        transform="rotate(90deg)"
                    />
                    <Icon
                        as={CornerIcon}
                        position="absolute"
                        w={3}
                        h={3}
                        bottom={0}
                        left={0}
                        transform="rotate(270deg)"
                    />
                    <Icon
                        as={CornerIcon}
                        position="absolute"
                        w={3}
                        h={3}
                        bottom={0}
                        right={0}
                        transform="rotate(180deg)"
                    />
                </>
            )}
            <Flex
                aspectRatio="auto"
                alignItems={splittedImages ? "stretch" : "center"}
                {...(hasHorizontalDirection && {
                    width: "100%",
                })}
            >
                {splittedImages ? (
                    <Flex
                        direction={hasHorizontalDirection ? "column" : "row"}
                        {...(withExpansion && {
                            height: [IMAGE_SIZE],
                            overflow: "hidden",
                            borderRadius: "xs",
                        })}
                        {...(hasHorizontalDirection && {
                            height: [IMAGE_SIZE],
                            width: ["100vw", IMAGE_SIZE * 2, "100%"],
                            maxW: ["420px"],
                        })}
                        onMouseLeave={onElementLeave}
                        onTouchEnd={onElementLeave}
                    >
                        <Flex
                            position="relative"
                            flex={
                                hasHorizontalDirection
                                    ? "none"
                                    : `0 0 calc(${splittedSizes[0]}*100%)`
                            }
                            height={
                                hasHorizontalDirection
                                    ? `${splittedSizes[0] * 100}%`
                                    : "auto"
                            }
                            transition={
                                hasHorizontalDirection
                                    ? "height .3s ease-in"
                                    : "flex-basis .3s ease-in"
                            }
                            borderRadius="sm"
                        >
                            <Picture
                                onMouseEnter={() => {
                                    withExpansion &&
                                        !isMobile &&
                                        onImageSelected(true);
                                }}
                                onTouchStart={(e) => {
                                    e.preventDefault();
                                    withExpansion && onImageSelected(true);
                                }}
                                onTouchEnd={onElementLeave}
                                onContextMenu={(e) => {
                                    e.preventDefault();
                                    return false;
                                }}
                                {...(image[0] as PictureProps)}
                                height={[IMAGE_SIZE]}
                                width={hasHorizontalDirection ? "100%" : "auto"}
                                objectPosition={
                                    hasHorizontalDirection ? "0 -90px" : "right"
                                }
                            />
                            {captionLeft && <Caption text={captionLeft} />}
                        </Flex>
                        <Flex
                            position="relative"
                            flex={
                                hasHorizontalDirection
                                    ? "none"
                                    : `0 0 calc(${splittedSizes[1]}*100%)`
                            }
                            height={
                                hasHorizontalDirection
                                    ? `${splittedSizes[1] * 100}%`
                                    : "auto"
                            }
                            transition={
                                hasHorizontalDirection
                                    ? "height .3s ease-in"
                                    : "flex-basis .3s ease-in"
                            }
                            borderRadius="sm"
                        >
                            <Picture
                                onMouseEnter={() => {
                                    withExpansion &&
                                        !isMobile &&
                                        onImageSelected(false);
                                }}
                                onTouchStart={(e) => {
                                    e.preventDefault();
                                    withExpansion && onImageSelected(false);
                                }}
                                onTouchEnd={onElementLeave}
                                onContextMenu={(e) => {
                                    e.preventDefault();
                                    return false;
                                }}
                                {...(image[1] as PictureProps)}
                                height={[IMAGE_SIZE]}
                                width={hasHorizontalDirection ? "100%" : "auto"}
                                objectPosition={
                                    hasHorizontalDirection ? "0 -90px" : "left"
                                }
                            />
                            {captionRight && <Caption text={captionRight} />}
                        </Flex>
                    </Flex>
                ) : (
                    <Picture
                        {...(image as PictureProps)}
                        {...(displayFrame && { p: 3 })}
                        {...(withFrame &&
                            !withFrameBorders && {
                                borderRadius: "sm",
                                overflow: "hidden",
                            })}
                    />
                )}
            </Flex>
        </Flex>
    );
};
